import { useEffect, useState } from 'react';

/**
 * FS-1079 — a label photo popped out of the console into its own browser
 * window: the console route it opens, which photos it may draw, and how a
 * Rotate pressed in the console reaches it.
 */

/** The console route that draws one label photo with no app chrome around it. */
export const LABEL_PHOTO_WINDOW_PATH = '/label-photo';

/**
 * Where the app stores label photos: Firebase Storage download URLs for the
 * project's bucket, as the equipment API writes them (`STORAGE_BUCKET` and
 * the download URL pattern in firebase-api's EquipmentController).
 */
const STORAGE_HOST = 'firebasestorage.googleapis.com';
const STORAGE_PHOTO_PATH = '/v0/b/filtersense-d79ca.firebasestorage.app/o/';

/** The photo is in the project's own photo storage: the only photos the window draws. */
export function isProjectStoragePhoto(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Not a URL at all, so not a stored photo.
    return false;
  }
  return parsed.protocol === 'https:' && parsed.host === STORAGE_HOST && parsed.pathname.startsWith(STORAGE_PHOTO_PATH);
}

/** The window's address for one photo drawn at one turn. */
export function labelPhotoWindowUrl(src: string, degrees: number): string {
  const query = new URLSearchParams({ src, turn: String(degrees) });
  return `${LABEL_PHOTO_WINDOW_PATH}?${query.toString()}`;
}

/** A Rotate in the console, told to any window showing the same photo. Same origin only. */
const TURN_CHANNEL = 'fluxhive-label-photo-turn';

interface TurnMessage {
  src: string;
  degrees: number;
}

/** Tell open photo windows that this photo is now drawn at this turn. */
export function announceTurn(src: string, degrees: number): void {
  const channel = new BroadcastChannel(TURN_CHANNEL);
  channel.postMessage({ src, degrees } satisfies TurnMessage);
  channel.close();
}

/** The turn to draw a photo at: the one the window opened with, then each one the console announces. */
export function useAnnouncedTurn(src: string | null, opened: number): number {
  const [degrees, setDegrees] = useState(opened);
  useEffect(() => {
    if (!src) return undefined;
    const channel = new BroadcastChannel(TURN_CHANNEL);
    channel.onmessage = (event: MessageEvent<TurnMessage>) => {
      if (event.data?.src === src && typeof event.data.degrees === 'number') setDegrees(event.data.degrees);
    };
    return () => channel.close();
  }, [src]);
  return degrees;
}
